/**
 * Notion-style campaign URL utilities.
 *
 * Every campaign slug stored in the DB is `{slug}-{shortId}` where shortId
 * is a 3-char base62 suffix deterministically derived from the campaign's
 * MongoDB ObjectID. This makes every campaign URL permanently unique:
 *
 *   "Gemini AI" (id: 507f191e...)  →  slug = "gemini-ai-kR7"
 *   "Gemini AI" (id: abc123ef...)  →  slug = "gemini-ai-mP9"
 *
 * Two campaigns with the same display name produce different slugs because
 * they have different ObjectIDs → different shortIds. The combined slug
 * `gemini-ai-kR7` is the DB-level unique key per brand — (brand_id, slug)
 * remains the unique index, the shortId suffix just guarantees it never
 * collides even when names are reused across deletions.
 *
 * Why base62: URL-safe alphanumeric (0–9 a–z A–Z). 3 chars = 62³ = 238 328
 * unique values per slug-base — negligible collision risk at any real scale.
 * Derivation: last 2 bytes of the 24-hex ObjectID → base62 encode, 3 chars.
 */

const B62 = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** The `$campaignParam` that opens a campaign: its slug, else its id (legacy campaigns have no slug). */
export function campaignRouteParam(campaign: { id: string; slug?: string }): string {
  return campaign.slug ?? campaign.id;
}

/**
 * Derive the 3-char base62 shortId from a 24-hex MongoDB ObjectID string.
 * The last 2 bytes (4 hex chars) are encoded; 62³ = 238 328 covers the
 * 65 535 possible 2-byte values with comfortable headroom.
 *
 * Must match the Go implementation in internal/campaigns/slug.go.
 */
export function campaignShortId(hexObjectId: string): string {
  // last 4 hex chars → last 2 bytes → value 0..65535
  // mod 62³ = 238328 to fit exactly 3 base62 chars
  let n = parseInt(hexObjectId.slice(-4), 16) % (62 * 62 * 62);
  let s = '';
  for (let i = 0; i < 3; i++) {
    s = B62[n % 62] + s;
    n = Math.floor(n / 62);
  }
  return s; // always exactly 3 chars, e.g. "kR7"
}

/**
 * Build the full Notion-style slug param from a display slug + ObjectID.
 * This is what gets stored in the DB and appears in the URL.
 *
 * @example
 * buildCampaignSlugParam("gemini-ai", "507f191e810c19729de860ea")
 * // → "gemini-ai-kR7"
 */
export function buildCampaignSlugParam(
  slug: string | undefined | null,
  objectId: string,
): string {
  const base = (slug ?? '').trim() || 'campaign';
  return `${base}-${campaignShortId(objectId)}`;
}
